import { watch } from 'vue';
import { storeToRefs } from 'pinia';
import { clone, deepEqual } from '@/core/utils';
import { notifyInfo, notifyError, notifySuccess } from '@/core/helpers/notification.helper';
import { useI18n } from 'vue-i18n';
import { useLiveEditorStore } from '@/modules/live-editor/stores/live-editor.store';
import { updatePageVersion } from '@/core/collections/pages/pages.service';
import type { JobBoard } from '@/core/collections/job-boards/job-board.interface';

export function useLiveEditor(currentJobBoard: Ref<JobBoard>) {
   const { t } = useI18n();
   const contentBlockStore = useLiveEditorStore();

   const { blocks, original, isDirty, isSavingChanges } = storeToRefs(contentBlockStore);

   watch(currentJobBoard, (newJobBoard) => {
      if (newJobBoard) {
         contentBlockStore.initializeBlocks(newJobBoard);
      }
   });

   function discardChanges() {
      contentBlockStore.setBlocks(original.value);
   }

   watch(
      blocks,
      (newBlocks) => {
         contentBlockStore.isDirty = !deepEqual(newBlocks, contentBlockStore.original);
      },
      { deep: true },
   );

   async function saveChanges() {
      isSavingChanges.value = true;
      try {
         notifyInfo({ title: t('job-board.settings.saving') });

         await updatePageVersion(currentJobBoard.value, blocks.value);

         original.value = clone(blocks.value); // saved blocks become the new original
         isDirty.value = false;

         notifySuccess({ title: t('job-board.settings.save-success') });
      } catch (_error) {
         notifyError({ title: t('job-board.settings.save-error') });
      } finally {
         isSavingChanges.value = false;
      }
   }

   return {
      discardChanges,
      saveChanges,
      ...storeToRefs(contentBlockStore),
   };
}
